/** Presentation state is deliberately independent of the build state machine. */
export const SHOW_CHAPTERS = ['Discover', 'Plan', 'Build', 'Check', 'Ready'] as const;
export type ShowChapter = typeof SHOW_CHAPTERS[number];

export type ShowArtifactKind = 'discovery' | 'image' | 'animation' | 'audio' | 'capture' | 'video' | 'bonus';
export type ShowArtifactCategory = 'identity' | 'world' | 'characters' | 'gameplay' | 'sound' | 'proof' | 'extras';
export type ShowArtifactOrigin = 'intent' | 'reference' | 'existing' | 'current-run';
export type ShowArtifactState = 'current' | 'selected' | 'recommended' | 'unused' | 'unavailable';
export type ShowBeatKind = 'identity' | 'world-cast' | 'gameplay-hook' | 'montage' | 'playable-proof';

export interface ShowArtifact {
  id: string;
  title: string;
  summary: string;
  kind: ShowArtifactKind;
  /** Variants in one group share a beat without losing their own identity. */
  group?: string;
  url?: string;
  /** Optional still paired with a sealed gameplay film. */
  posterUrl?: string;
  label?: string;
  unavailable?: boolean;
  /** A real output of this game. Only these appear in Collection; the film also screens ideas and Deej. */
  gameAsset?: boolean;
  strip?: import('@djgg/creations').CreationFilmStripV1;
  /** Optional semantic metadata. Adapters can add this as publication data becomes richer. */
  category?: ShowArtifactCategory;
  origin?: ShowArtifactOrigin;
  state?: ShowArtifactState;
  beat?: ShowBeatKind;
  version?: number;
}

export interface ShowScene {
  id: string;
  kind: ShowBeatKind;
  artifacts: ShowArtifact[];
}

export function shortCopy(value: string, limit = 140): string {
  const clean = value.replace(/\s+/g, ' ').trim();
  if (clean.length <= limit) return clean;
  return `${clean.slice(0, limit - 1).replace(/\s+\S*$/, '')}…`;
}

/** What Collection shows: the game's real outputs that can actually be opened. */
export function collectionArtifacts(artifacts: readonly ShowArtifact[]): ShowArtifact[] {
  return artifacts.filter(item => item.gameAsset && item.url && !item.unavailable);
}

function includesAny(value: string, terms: readonly string[]): boolean {
  const normalized = value.toLowerCase();
  return terms.some(term => normalized.includes(term));
}

/** A safe display category when the publishing adapter has not supplied one. */
export function artifactCategory(artifact: ShowArtifact): ShowArtifactCategory {
  if (artifact.category) return artifact.category;
  if (artifact.kind === 'audio') return 'sound';
  if (artifact.kind === 'capture') return 'proof';
  if (artifact.kind === 'bonus') return 'extras';
  const hint = `${artifact.group ?? ''} ${artifact.title}`;
  if (includesAny(hint, ['cover', 'title', 'logo', 'identity'])) return 'identity';
  if (includesAny(hint, ['background', 'world', 'scene', 'environment', 'palette'])) return 'world';
  if (includesAny(hint, ['hero', 'player', 'enemy', 'character', 'sprite', 'avatar'])) return 'characters';
  if (artifact.kind === 'discovery') return 'gameplay';
  return 'extras';
}

function beatFor(artifact: ShowArtifact): ShowBeatKind {
  if (artifact.beat) return artifact.beat;
  switch (artifactCategory(artifact)) {
    case 'identity': return 'identity';
    case 'world':
    case 'characters': return 'world-cast';
    case 'gameplay': return 'gameplay-hook';
    case 'proof': return 'playable-proof';
    default: return 'montage';
  }
}

const BEAT_ORDER: readonly ShowBeatKind[] = ['identity', 'world-cast', 'gameplay-hook', 'montage', 'playable-proof'];

/**
 * Curate a few game-first beats. Every artifact is retained, variants share a
 * beat, and explicit semantic metadata wins over filename heuristics.
 */
export function showScenes(artifacts: readonly ShowArtifact[]): ShowScene[] {
  const unique = [...new Map(artifacts.map(item => [item.id, item])).values()];
  const groups = new Map<string, { kind: ShowBeatKind; items: ShowArtifact[]; first: number }>();
  unique.forEach((artifact, index) => {
    const kind = beatFor(artifact);
    const key = `${kind}:${artifact.group ?? artifact.id}`;
    const existing = groups.get(key);
    if (existing) existing.items.push(artifact);
    else groups.set(key, { kind, items: [artifact], first: index });
  });
  return [...groups.entries()]
    .sort(([, a], [, b]) => BEAT_ORDER.indexOf(a.kind) - BEAT_ORDER.indexOf(b.kind) || a.first - b.first)
    .flatMap(([key, group]) => {
      const scenes: ShowScene[] = [];
      for (let i = 0; i < group.items.length; i += 3) {
        scenes.push({ id: `${key}:${i / 3}`, kind: group.kind, artifacts: group.items.slice(i, i + 3) });
      }
      return scenes;
    });
}

/** Unseen material wins over repeats; array position never identifies a scene. */
export function nextShowScene(scenes: readonly ShowScene[], seen: ReadonlySet<string>, current: string | null): string | null {
  const unseen = scenes.find(scene => scene.artifacts.some(artifact => !seen.has(artifact.id)) && scene.id !== current);
  if (unseen) return unseen.id;
  if (!scenes.length) return null;
  return scenes[(scenes.findIndex(scene => scene.id === current) + 1) % scenes.length]!.id;
}

export function buildShowChapter(phase: string | undefined, ready: boolean): ShowChapter {
  if (ready) return 'Ready';
  return phase && ['gates', 'review', 'quality', 'audit', 'publishing'].includes(phase) ? 'Check' : 'Build';
}
